import {useEffect, useMemo} from "react";
import {useAuth} from "react-oidc-context";
import {Provider} from "react-redux";
import {Redirect, Switch} from "react-router-dom";
import {ROLES} from "./consts";
import Admins from "./containers/admin/admins";
import BillingInfo from "./containers/billingInfo/billingInfo";
import Client from "./containers/client/client";
import GasStations from "./containers/gasStation/gasStations";
import Managers from "./containers/manager/managers";
import Owners from "./containers/owner/owners";
import Blacklist from "./containers/parkingZone/blacklist";
import ParkingZones from "./containers/parkingZone/parkingZones";
import Whitelist from "./containers/parkingZone/whitelist";
import Report from "./containers/report/report";
import Requests from "./containers/requests/requests";
import TicketSeries from "./containers/ticket/ticketSeries";
import Unauthorized from "./containers/unauthorized/unauthorized";
import Users from "./containers/users/users";
import Viewers from "./containers/viewer/viewers";
import DriveOff from "./containers/violations/driveOff";
import Violations from "./containers/violations/violations";
import LoadingIndicator from "./reusable/loadingIndicator";
import {store} from "./store";
import useGaTracker from "./useGaTracker";
import {AuthorizedRoute} from "./utils/AuthorizedRoute";
import {resolveRedirectLocation} from "./utils/resolveRedirectLocation";
import {UnauthorizedRoute} from "./utils/UnauthorizedRoute";

export default function AppInner() {
    useGaTracker();
    const auth = useAuth();
    const { isLoading,isAuthenticated, signinRedirect} = auth;

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            console.log("Not authenticated, redirecting to login page");
            signinRedirect({redirect_uri: window.location.origin});
        }
    }, [isLoading, isAuthenticated, signinRedirect]);


    // Memoize the app content so it doesn't rerender when other auth props change
    const appContent = useMemo(() => {
        const redirectResult = resolveRedirectLocation(auth);

        return (
            <Provider store={store}>
                <Switch>
                    <Redirect exact from="/" to={redirectResult}/>

                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                            ROLES.PARKING_VIEWER
                        ]}
                        path="/parkingZones"
                        component={ParkingZones}
                    />

                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.GAS_STATION_ADMIN,
                            ROLES.GAS_STATION_MANAGER,
                            ROLES.GAS_STATION_VIEWER
                        ]}
                        path="/gasStations"
                        component={GasStations}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                            ROLES.GAS_STATION_ADMIN,
                            ROLES.GAS_STATION_MANAGER,
                            ROLES.PARKING_VIEWER,
                            ROLES.GAS_STATION_VIEWER
                        ]}
                        path="/owners"
                        component={Owners}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN]}
                        path="/admins"
                        component={Admins}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                            ROLES.PARKING_VIEWER
                        ]}
                        path="/violations"
                        component={Violations}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                            ROLES.GAS_STATION_VIEWER
                        ]}
                        path="/driveOff"
                        component={DriveOff}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN]}
                        path="/ticketSeries"
                        component={TicketSeries}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN]}
                        path="/users"
                        component={Users}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                            ROLES.GAS_STATION_ADMIN,
                            ROLES.GAS_STATION_MANAGER,
                        ]}
                        path="/requests"
                        component={Requests}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                        ]}
                        path="/whitelist/:id"
                        component={Whitelist}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.PARKING_MANAGER,
                        ]}
                        path="/blacklist/:id"
                        component={Blacklist}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.GAS_STATION_ADMIN,
                        ]}
                        path="/managers"
                        component={Managers}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[
                            ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN,
                            ROLES.GAS_STATION_ADMIN
                        ]}
                        path="/viewers"
                        component={Viewers}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN]}
                        path="/billingInfo"
                        component={BillingInfo}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN,
                            ROLES.PARKING_ADMIN, ROLES.PARKING_MANAGER, ROLES.PARKING_VIEWER,
                            ROLES.GAS_STATION_ADMIN, ROLES.GAS_STATION_MANAGER, ROLES.GAS_STATION_VIEWER]}
                        path="/report/:reportType"
                        component={Report}
                    />
                    <AuthorizedRoute
                        exact
                        roles={[ROLES.SYSADMIN]}
                        path="/client"
                        component={Client}
                    />
                    <UnauthorizedRoute exact path="/unauthorized" component={Unauthorized}/>
                    <Redirect to={redirectResult}/>
                </Switch>
            </Provider>);
    }, [isAuthenticated]);

    // If auth is still loading, show loading state
    if (!isAuthenticated) {
        console.log("Auth is still loading...");
        return <LoadingIndicator/>;
    }

    return isAuthenticated ? appContent : <LoadingIndicator/>;
}